'use client';

import type { Transition } from 'framer-motion';
import { motion } from 'framer-motion';

import { Image } from '@/components/client';
import { getStaticCdnUrl } from '@/utils';

// 애니메이션 상수
const ANIMATION = {
  DURATION: 5, // 총 5초 (가운데 멈춤 2초 + 이동 3초)
  EASE: 'easeInOut',
  SHAKE: {
    ROTATE: 0.8,
    Y: 1.5,
  },
} as const;

// 공통 트랜지션
const baseTransition: Transition = {
  duration: ANIMATION.DURATION,
  ease: ANIMATION.EASE,
  repeat: Infinity,
  repeatType: 'loop',
};

// 트럭 이동 애니메이션
// 0~0.2: 트럭1 멈춤, 트럭2 바깥 대기
// 0.2~0.5: 트럭1 나감, 트럭2 들어옴 (동시)
// 0.5~0.7: 트럭1 바깥 대기, 트럭2 멈춤
// 0.7~1: 트럭1 들어옴, 트럭2 나감 (동시)
const truckMoveVariants = {
  truck1: {
    x: ['0%', '0%', '120%', '-120%', '-120%', '0%'],
    times: [0, 0.2, 0.5, 0.5, 0.7, 1],
  },
  truck2: {
    x: ['-120%', '-120%', '0%', '0%', '120%'],
    times: [0, 0.2, 0.5, 0.7, 1],
  },
};

// 트럭 흔들림 애니메이션
// 트럭1: 0~0.2 흔들림 (repeat되면 끝과 시작이 연결되어 연속 멈춤)
// 트럭2: 0.5~0.7 흔들림
const truckShakeVariants = {
  truck1: {
    animate: {
      rotate: [
        0,
        -ANIMATION.SHAKE.ROTATE,
        ANIMATION.SHAKE.ROTATE,
        -ANIMATION.SHAKE.ROTATE,
        ANIMATION.SHAKE.ROTATE,
        0,
      ],
      y: [0, -ANIMATION.SHAKE.Y, 0, -ANIMATION.SHAKE.Y, 0, 0],
    },
    transition: {
      duration: ANIMATION.DURATION,
      ease: ANIMATION.EASE,
      repeat: Infinity,
      times: [0, 0.05, 0.1, 0.15, 0.2, 1],
    } as Transition,
  },
  truck2: {
    animate: {
      rotate: [
        0,
        0,
        -ANIMATION.SHAKE.ROTATE,
        ANIMATION.SHAKE.ROTATE,
        -ANIMATION.SHAKE.ROTATE,
        ANIMATION.SHAKE.ROTATE,
        0,
        0,
      ],
      y: [0, 0, -ANIMATION.SHAKE.Y, 0, -ANIMATION.SHAKE.Y, 0, 0, 0],
    },
    transition: {
      duration: ANIMATION.DURATION,
      ease: ANIMATION.EASE,
      repeat: Infinity,
      times: [0, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 1],
    } as Transition,
  },
};

const TRUCK_IMAGE_SRC = getStaticCdnUrl('/donation/guide-truck.png');

type TruckVariant = keyof typeof truckMoveVariants;

interface AnimatedTruckProps {
  variant: TruckVariant;
}

const AnimatedTruck = ({ variant }: AnimatedTruckProps) => {
  const move = truckMoveVariants[variant];
  const shake = truckShakeVariants[variant];

  return (
    <motion.div
      className="absolute inset-0 will-change-transform"
      animate={{ x: move.x }}
      transition={{ ...baseTransition, times: move.times }}
    >
      <motion.div
        className="relative size-full origin-bottom will-change-transform"
        animate={shake.animate}
        transition={shake.transition}
      >
        <Image
          src={TRUCK_IMAGE_SRC}
          alt="무료 배송 트럭 이미지"
          fill
          sizes="(max-width: 1279px) 100vw, 600px"
          className="object-contain"
        />
      </motion.div>
    </motion.div>
  );
};

const FreeShippingSection = () => {
  return (
    <section className="flex w-full flex-col items-center gap-28 py-120 mobile:py-60">
      {/* 타이틀 */}
      <div className="text-center font-800 typo-h2 mobile:font-700 mobile:typo-h5">
        <p className="text-O-500">배송비 전액 무료</p>
        <p className="text-Gray-black">기부금 외 추가 부담 없이</p>
        <p className="text-Gray-black">바로 수령 가능해요</p>
      </div>

      {/* 배송 트럭 이미지 */}
      <div className="relative h-206 w-full overflow-hidden mobile:h-103">
        <AnimatedTruck variant="truck1" />
        <AnimatedTruck variant="truck2" />
      </div>
    </section>
  );
};

export default FreeShippingSection;
